import React, { Component } from 'react';
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { Redirect, Route, Switch } from 'react-router';
import { FullScreenError } from '../app-booking/FullScreenError';
import Button from '../components/Button';
import { ImmediateRequest, makeRequest, RequestContext, RequestState } from '../components/NetworkRequest';
import * as Icons from '../components/SVGIcons';
import { track } from '../NylasWindowContext';
import { Scheduling } from '../types/Scheduling';
import { clone, EmbedConfig } from '../Utils';
import './MainView.scss';
import { PageDetailView } from './PageDetailView';
import { PagesGridView } from './PagesGridView';
import { PageWizardView } from './PageWizardView';

interface EmbedMainViewProps {
  config: EmbedConfig;
}
interface EmbedMainViewState {
  pages: RequestState<Scheduling.Page[]>;
}

function trackPageEdits(prev: Scheduling.Page, next: Scheduling.Page) {
  if (prev.slug !== next.slug) {
    track('Scheduling Page Config Changed', { label: 'Slug' });
  }
  if (JSON.stringify(prev.config.appearance) !== JSON.stringify(next.config.appearance)) {
    track('Scheduling Page Config Changed', { label: 'Appearance' });
  }
  if (JSON.stringify(prev.config.booking) !== JSON.stringify(next.config.booking)) {
    track('Scheduling Page Config Changed', { label: 'Booking' });
  }
  if (JSON.stringify(prev.config.calendar_ids) !== JSON.stringify(next.config.calendar_ids)) {
    track('Scheduling Page Config Changed', { label: 'Calendars' });
  }
  if (JSON.stringify(prev.config.reminders) !== JSON.stringify(next.config.reminders)) {
    track('Scheduling Page Config Changed', { label: 'Reminders' });
  }
  if (JSON.stringify(prev.config.event) !== JSON.stringify(next.config.event)) {
    track('Scheduling Page Config Changed', { label: 'Event Details' });
  }
  if (JSON.stringify(prev.config.timezone) !== JSON.stringify(next.config.timezone)) {
    track('Scheduling Page Config Changed', { label: 'Timezone' });
  }
}

interface DefaultConfigProps {
  config: EmbedConfig;
  children: (config: EmbedConfig) => React.ReactElement<any>;
}

const DefaultConfig = (props: DefaultConfigProps) => (
  <ImmediateRequest<Scheduling.DefaultConfigResponse, {}> path={`/config`}>
    {(response) => {
      if (response instanceof Error) {
        window.parent.postMessage({ type: 'nylas:ready' }, '*');
        return <InvalidAccessToken message={response.message} />;
      }

      if (response === 'loading' || response === 'empty') {
        return (
          <div className="loading-wrap">
            <div style={{ color: '#666', margin: 'auto' }}>
              <Icons.Spinner width={40} height={40} />
            </div>
          </div>
        );
      }

      const data = response as Scheduling.DefaultConfigResponse;
      const token = data.token;
      const updatedConfig = {
        ...props.config,
        token,
        defaults: {
          features: {
            ...data.config.features,
          },
          ...props.config.defaults,
        },
      };
      return props.children(updatedConfig);
    }}
  </ImmediateRequest>
);

interface InvalidAccessTokenProps {
  message?: string;
}
const InvalidAccessToken: React.FunctionComponent<InvalidAccessTokenProps> = (props: InvalidAccessTokenProps) => {
  const intl = useIntl();
  return (
    <FullScreenError
      message={
        intl.formatMessage({
          id: 'error-invalid-access-token',
          defaultMessage: 'Unable to load config. Check your access token.',
          description: 'Error - Unable to load config',
        }) + ` (error: ${props.message ?? 'unknown'})`
      }
    />
  );
};

export class MainView extends Component<EmbedMainViewProps, EmbedMainViewState> {
  static contextType = RequestContext;

  state: EmbedMainViewState = {
    pages: 'loading',
  };

  async componentDidMount() {
    await this.fetchPages();

    // we're typically displayed within an iFrame in the nylas customer application.
    // Now that we've mounted it's safe to transition from the iframe's loading spinner
    // to the content. Let's do this!
    window.parent.postMessage({ type: 'nylas:ready' }, '*');

    track('Scheduling Embed Displayed', {
      pageDomain: this.props.config.pageDomain,
    });
  }

  fetchPages = async () => {
    await makeRequest<Scheduling.Page[]>(this.context, `/manage/pages`).then(
      (result) => this.setState({ pages: result }),
      (error) => this.setState({ pages: error }),
    );
  };

  upsertPage = async (fields: Scheduling.PageCreatePayload | Scheduling.Page) => {
    const clonedFields = clone(fields); // Cloning because we're going to be mutating the fields
    // When editing a page, we don't want to send access_tokens to the server.
    if ('id' in clonedFields && typeof clonedFields.access_token_infos !== 'undefined') {
      delete clonedFields.access_token_infos;
    }

    // hasFormErrors is not a valid API field, so it must be discarded
    // even if false prior to submission.
    if (!clonedFields.config?.hasFormErrors) {
      delete clonedFields.config?.hasFormErrors;
    }

    const promise =
      'id' in clonedFields
        ? makeRequest<Scheduling.Page>(this.context, `/manage/pages/${clonedFields.id}`, 'PUT', {
            name: clonedFields.name,
            slug: clonedFields.slug,
            config: clonedFields.config,
          })
        : makeRequest<Scheduling.Page>(this.context, `/manage/pages`, 'POST', clonedFields);
    const page = await promise;
    if (this.state.pages instanceof Array) {
      this.setState({ pages: this.state.pages.filter((p) => p.id !== page.id).concat([page]) });
    }
  };

  deletePage = async (page: Scheduling.Page) => {
    track('Scheduling Page Deleted', {});

    try {
      await makeRequest<Scheduling.Page>(this.context, `/manage/pages/${page.id}`, 'DELETE');
    } catch (err: any) {
      alert(`Sorry, we were unable to delete this page.\n\n${err.toString()}`);
    }
    if (this.state.pages instanceof Array) {
      this.setState({ pages: this.state.pages.filter((p) => p.id !== page.id) });
    }
  };

  onDismiss = () => {
    window.parent.postMessage({ type: 'nylas:close-modal' }, '*');
  };

  render() {
    const { config } = this.props;
    const { pages } = this.state;

    if (pages === 'loading' || pages === 'empty') {
      // our loading state is provided by the containing modal.
      return <div />;
    }
    if (pages instanceof Error) {
      return (
        <MainViewError
          title={defineMessage({
            defaultMessage: 'Sorry, we were unable to load your pages.',
            description: 'Error - Unable to load pages',
            id: 'e-utlp',
          })}
          message={pages.message}
          onRetry={this.fetchPages}
        />
      );
    }

    const allowGridView = 'accessToken' in config.auth;

    return (
      <Switch>
        <Route
          path="/embed/add-page"
          render={({ history }) => {
            const onExitDetailView = () => {
              if (allowGridView) {
                history.push('/embed/pages');
              } else {
                this.onDismiss();
              }
            };
            return (
              <DefaultConfig config={config}>
                {(defaultConfig) => (
                  <PageWizardView
                    config={defaultConfig}
                    onDismiss={this.onDismiss}
                    onCreatePage={async (pageSlug, pageConfig) => {
                      // no way to represent Partial<PageConfig> in types auto-generated
                      // from the swagger documentation
                      try {
                        await this.upsertPage({
                          slug: pageSlug,
                          config: pageConfig,
                          access_tokens: [(config.auth as any).accessToken],
                        });
                        track('Scheduling Page Created', {});
                      } catch (err) {
                        return (err as Error).toString();
                      }
                      onExitDetailView();
                      return;
                    }}
                  />
                )}
              </DefaultConfig>
            );
          }}
        />
        <Route
          path="/embed/pages"
          render={() =>
            allowGridView ? (
              <PagesGridView
                pages={pages}
                config={config}
                pageDomain={config.pageDomain}
                onDismiss={this.onDismiss}
                onDeletePage={this.deletePage}
              />
            ) : (
              <Redirect to={`/embed/${pages[0].id}`} />
            )
          }
        />
        <Route
          path="/embed/:id"
          render={({ match, history }) => {
            const page = pages.find((p) => p.id / 1 === +match.params.id / 1);
            const onExitDetailView = () => {
              if (allowGridView) {
                history.push('/embed/pages');
              } else {
                this.onDismiss();
              }
            };
            return page ? (
              <PageDetailView
                config={config}
                initialPage={page}
                allowBack={allowGridView}
                onDismiss={this.onDismiss}
                onBack={() => history.push('/embed/pages')}
                onDelete={
                  allowGridView
                    ? async () => {
                        onExitDetailView();
                        await this.deletePage(page);
                        this.setState({ pages: pages.filter((p) => p !== page) });
                      }
                    : undefined
                }
                onSave={async (updated) => {
                  trackPageEdits(page, updated);
                  track('Scheduling Page Saved', {});
                  try {
                    await this.upsertPage(updated);
                  } catch (err: any) {
                    alert(`Sorry, we were unable to create the scheduling page.\n\n${err.toString()}`);
                    return;
                  }
                  onExitDetailView();
                }}
              />
            ) : (
              <MainViewError
                title={defineMessage({
                  defaultMessage: 'Sorry, this scheduling page does not exist.',
                  description: 'Error - Page does not exist',
                  id: 'e-pdne',
                })}
                onRetry={this.fetchPages}
              />
            );
          }}
        />
        <Redirect to="/embed/pages" />
      </Switch>
    );
  }
}

export const MainViewError: React.FunctionComponent<{
  title: MessageDescriptor;
  message?: string;
  onRetry: () => void;
}> = (props) => {
  const intl = useIntl();
  return (
    <div className="MainViewError">
      <Icons.UhOhFail width="160" height="160" />
      <h3>{intl.formatMessage(props.title)}</h3>
      <div style={{ marginBottom: 30 }}>{props.message}</div>
      <Button onClick={props.onRetry}>
        <FormattedMessage defaultMessage="Try Again" description="Try Again" id="Try-Again" />
      </Button>
    </div>
  );
};
